/**
 * Project Picker Dialog
 * Modal dialog for selecting a project, reusable across the app
 */

import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { colorPalette } from '../../theme';

const ProjectPickerDialog = ({ open, onClose, projects, onProjectSelect, currentProjectId }) => {
  const handleProjectClick = (projectId) => {
    onProjectSelect(projectId);
    onClose();
  };

  // ASS-84: memoize filtered visible projects (active + draft; inactive
  // stays hidden) to avoid recomputation on every render. Matches
  // ProjectListPage.jsx's and ProjectYardSelector.jsx's rule so a Draft
  // project is selectable everywhere a project can be selected, not just
  // in the main list.
  const visibleProjects = useMemo(() => 
    projects.filter(p => p.status === 'active' || p.status === 'draft'),
    [projects]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colorPalette.backgroundColor,
          border: `1px solid ${colorPalette.lightSteel}`,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>
          Select Project
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{ ml: 2 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <List sx={{ py: 0 }}>
          {visibleProjects.length === 0 ? (
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2" color="text.secondary" align="center">
                    No active projects available
                  </Typography>
                }
              />
            </ListItem>
          ) : (
            visibleProjects.map((project) => (
              <ListItem
                key={project.id}
                disablePadding
                sx={{
                  backgroundColor: currentProjectId === project.id ? `${colorPalette.asseTracOrange}1A` : 'transparent',
                  borderLeft: currentProjectId === project.id ? `3px solid ${colorPalette.asseTracOrange}` : 'none',
                }}
              >
                <ListItemButton
                  onClick={() => handleProjectClick(project.id)}
                  sx={{
                    py: 2,
                    '&:hover': {
                      backgroundColor: `${colorPalette.asseTracOrange}0D`,
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" fontWeight={currentProjectId === project.id ? 600 : 400}>
                          {project.name}
                        </Typography>
                        {project.type === 'yard' && (
                          <Typography variant="caption" color="text.secondary">
                            (Yard)
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      project.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {project.description}
                        </Typography>
                      )
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectPickerDialog;
